import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { t } from '../lib/i18n';

interface ToastItem {
  id: number;
  text: string;
}

const ToastContext = createContext<(text: string) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setItems((l) => l.filter((i) => i.id !== id)), []);
  const push = useCallback(
    (text: string) => {
      const id = next.current++;
      setItems((l) => [...l.slice(-2), { id, text }]);
      window.setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        role="region"
        aria-label={t('common.notifications')}
        className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-center gap-2 sm:items-end"
      >
        {items.map((i) => (
          <div
            key={i.id}
            role="status"
            className="cs-toast pointer-events-auto flex max-w-sm items-center gap-3 rounded-lg bg-slate-900 px-4 py-3 text-sm text-white shadow-lg"
          >
            <CheckCircle2 aria-hidden="true" className="h-5 w-5 shrink-0 text-emerald-400" />
            <span>{i.text}</span>
            <button
              type="button"
              aria-label={t('common.dismiss')}
              className="-mr-1 inline-flex h-6 w-6 items-center justify-center rounded text-slate-300 hover:text-white"
              onClick={() => dismiss(i.id)}
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (text: string) => void {
  return useContext(ToastContext);
}

/** Shows a toast each time `success` turns true (for example a mutation's isSuccess). */
export function useToastOnSuccess(success: boolean, text: string): void {
  const push = useToast();
  const was = useRef(false);
  useEffect(() => {
    if (success && !was.current) push(text);
    was.current = success;
  }, [success, text, push]);
}
